/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { memo } from 'react';

import commandCloudIcon from '../../assets/command-cloud-icon.svg';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';

import { getCommandIntelligenceWelcomeHeroStyles } from './CommandIntelligenceWelcomeHero.styles';

const isFeaturedSuggestionKey = key =>
  key === 'static-summarize-all-orders' || key === 'static-summarize-attached-orders';

const CommandIntelligenceWelcomeHero = ({
  title,
  subtitle,
  promptsLabel,
  suggestions = [],
  onSelectSuggestion,
  isSuggestionDisabled = false,
  intentSlot = null,
  attachHint = null,
  isHorizontalDock = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 950px)');
  const styles = getCommandIntelligenceWelcomeHeroStyles(theme, { isMobile, isHorizontalDock });
  const logoSize = isMobile ? 72 : 96;

  if (isHorizontalDock) {
    return (
      <Box sx={styles.root}>
        <Box sx={styles.content}>
          <Box sx={styles.leading}>
            <Typography component="h2" variant="subtitle1" sx={styles.title}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" sx={styles.subtitle}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          {intentSlot ? <Box sx={styles.intentSlot}>{intentSlot}</Box> : null}

          {!intentSlot && suggestions.length > 0 ? (
            <Box sx={styles.promptsScroll}>
              {suggestions.map(item => {
                const isFeatured = isFeaturedSuggestionKey(item.key);

                return (
                  <Button
                    key={item.key}
                    type="button"
                    variant="outlined"
                    disabled={isSuggestionDisabled}
                    onClick={() => onSelectSuggestion(item.promptText)}
                    sx={[styles.promptButton, isFeatured ? styles.promptButtonFeatured : null]}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          ) : null}

          {attachHint ? (
            <Typography component="p" variant="caption" sx={styles.attachHint}>
              {attachHint}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.content}>
        <Box sx={styles.logoWrap}>
          <img src={commandCloudIcon} alt="" width={logoSize} height={logoSize} style={{ display: 'block' }} />
        </Box>

        <Box sx={styles.titleWrapper}>
          <Typography component="h2" variant="h2" sx={styles.title}>
            {title}
          </Typography>
        </Box>

        <Typography variant="body1" sx={styles.subtitle}>
          {subtitle}
        </Typography>

        {intentSlot ? <Box sx={styles.intentSlot}>{intentSlot}</Box> : null}

        {!intentSlot && suggestions.length > 0 ? (
          <>
            <Typography component="p" sx={styles.promptsLabel}>
              {promptsLabel}
            </Typography>

            <Box sx={styles.promptsGrid}>
              {suggestions.map(item => {
                const isFeatured = isFeaturedSuggestionKey(item.key);

                return (
                  <Button
                    key={item.key}
                    type="button"
                    variant="outlined"
                    disabled={isSuggestionDisabled}
                    onClick={() => onSelectSuggestion(item.promptText)}
                    sx={[styles.promptButton, isFeatured ? styles.promptButtonFeatured : null]}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          </>
        ) : null}

        {attachHint ? (
          <Typography component="p" variant="caption" sx={styles.attachHint}>
            {attachHint}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default memo(CommandIntelligenceWelcomeHero);
